export type LiveTranscriptRole = "user" | "assistant";

export interface LiveTranscriptLine {
  turnId: string;
  role: LiveTranscriptRole;
  text: string;
  isFinal: boolean;
  ts?: string;
  sessionId?: string;
}

export interface LiveTranscriptSocketPayload {
  role?: string;
  text?: string;
  is_final?: boolean;
  isFinal?: boolean;
  turn_id?: string;
  turnId?: string;
  ts?: string;
  record_id?: string;
  session_id?: string;
}

const normalizeRole = (raw: unknown): LiveTranscriptRole | null => {
  if (typeof raw !== "string") return null;
  const value = raw.trim().toLowerCase();
  if (value === "user" || value === "learner" || value === "human") {
    return "user";
  }
  if (
    value === "assistant" ||
    value === "ai" ||
    value === "avatar" ||
    value === "hcp" ||
    value === "coach" ||
    value === "bot" ||
    value === "doctor"
  ) {
    return "assistant";
  }
  return null;
};

// Parse one socket payload into a store line, or null if invalid.
export const parseLiveTranscriptPayload = (
  payload: unknown,
): LiveTranscriptLine | null => {
  if (!payload || typeof payload !== "object") return null;
  const raw = payload as LiveTranscriptSocketPayload;
  const text = typeof raw.text === "string" ? raw.text.trim() : "";
  if (!text) return null;

  const role = normalizeRole(raw.role);
  if (!role) return null;

  const turnId =
    (typeof raw.turn_id === "string" && raw.turn_id.trim()) ||
    (typeof raw.turnId === "string" && raw.turnId.trim()) ||
    null;
  // turn_id is required by BE contract — ignore malformed events without it.
  if (!turnId) return null;

  const isFinal =
    typeof raw.is_final === "boolean"
      ? raw.is_final
      : typeof raw.isFinal === "boolean"
        ? raw.isFinal
        : true;

  return {
    turnId,
    role,
    text,
    isFinal,
    ts: typeof raw.ts === "string" ? raw.ts : undefined,
    sessionId:
      typeof raw.session_id === "string" && raw.session_id.trim()
        ? raw.session_id.trim()
        : undefined,
  };
};

const sortByTimestamp = (
  lines: LiveTranscriptLine[],
): LiveTranscriptLine[] => {
  return [...lines].sort((a, b) => {
    const left = a.ts ? Date.parse(a.ts) : NaN;
    const right = b.ts ? Date.parse(b.ts) : NaN;
    if (Number.isFinite(left) && Number.isFinite(right)) {
      return left - right;
    }
    // Keep relative receive order when timestamps are missing.
    return 0;
  });
};

/**
 * Upsert by turn_id:
 * - partials replace in place
 * - finals lock the line
 * - role mismatch / post-final updates are ignored
 * - original ts is preserved on subsequent updates
 * - sorted by ts when both sides have valid timestamps
 */
export const upsertLiveTranscriptLine = (
  lines: LiveTranscriptLine[],
  next: LiveTranscriptLine,
): LiveTranscriptLine[] => {
  const index = lines.findIndex((line) => line.turnId === next.turnId);

  if (index >= 0) {
    const existing = lines[index];
    if (existing.isFinal) return lines;
    if (existing.role !== next.role) return lines;

    const updated = [...lines];
    updated[index] = {
      ...next,
      ts: existing.ts || next.ts,
    };
    return sortByTimestamp(updated);
  }

  return sortByTimestamp([...lines, next]);
};

// True when an event belongs to a different, already-known session.
export const isStaleTranscriptSession = (
  knownSessionId: string | null | undefined,
  eventSessionId: string | null | undefined,
): boolean => {
  if (!knownSessionId || !eventSessionId) return false;
  return knownSessionId !== eventSessionId;
};
